import classNames from 'clsx';
export { default as classNames } from 'clsx';
export { default as useResizeObserver } from 'use-resize-observer';
import { forwardRef, useRef, useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { jsx } from 'react/jsx-runtime';

/**
 * Combines multiple React refs into a single ref.
 * This may be used e.g. to attach both a forwarded ref and a local ref
 * to the same DOM element.
 *
 * @example
 * React.forwardRef((props, forwardedRef) => {
 * 	const ref = React.useRef();
 * 	return <div ref={combineRefs(ref, forwardedRef)} />;
 * });
 */
function combineRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return value => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        ref.current = value;
      }
    }
  };
}

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Dialog-module_no-scroll__pPSuI {\n  overflow: hidden;\n}";
var styles = {"no-scroll":"Dialog-module_no-scroll__pPSuI"};
styleInject(css_248z);

/**
 * Internal only. Try to avoid sniffing user agents where possible.
 */
function use_UNSAFEUserAgentInfo() {
  var _window;
  if (typeof window === 'undefined') {
    return {
      browser: 'unknown',
      formFactor: 'unknown',
      userAgent: ''
    };
  }
  const userAgent = (_window = window) === null || _window === void 0 || (_window = _window.navigator) === null || _window === void 0 ? void 0 : _window.userAgent;
  return {
    browser: getBrowser(userAgent),
    formFactor: getFormFactor(userAgent),
    userAgent
  };
}
function getBrowser(input) {
  if (!input) {
    return 'unknown';
  }

  // This isn't meant to be super robust. The order of these matters.
  // Edge desktop: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/107.0.0.0 Safari/537.36 Edg/107.0.1418.35'
  // Chrome desktop: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/107.0.0.0 Safari/537.36'
  // Firefox desktop: "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:107.0) Gecko/20100101 Firefox/107.0"
  // Safari desktop: "Mozilla/5.0 (Macintosh; Intel Mac OS x 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.1.2 Safari/605.1.15"
  // Edge android:
  // Chrome android: "Mozilla/5.0 (Linux; Android 11; LE2113) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/105.0.0.0 Mobile Safari/537.36"
  // Firefox android: "Mozilla/5.0 (Android 11; Mobile; rv:102) Gecko/102.0 Firefox/102.0"
  // Safari android: N/A
  // Edge IOS:
  // Chrome IOS:
  // Firefox IOS:
  // Safari IOS: "Mozilla/5.0 (iPhone; CPU iPhone OS 16_0_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.0 Mobile/15E148 Safari/604.1"

  if (input.includes('Edg')) {
    return 'edge';
  }
  if (input.includes('Chrome')) {
    return 'chrome';
  }
  if (input.includes('Firefox')) {
    return 'firefox';
  }
  if (input.includes('Safari')) {
    return 'safari';
  }
  return 'unknown';
}
function getFormFactor(input) {
  if (!input) {
    return 'unknown';
  }
  if (input.includes('Mobile')) {
    return 'mobile';
  }
  return 'desktop';
}

function openDialog(dialog, modal, focusOnOpen) {
  if (modal) {
    dialog.showModal();
  } else {
    if (!focusOnOpen) {
      /*
      A pile of hacks to make sure that the currently focused element doesn't change as a
      result of calling dialog.show().
      As of 2024-02 it seems only Chrome needs the event prevention; setting `inert` seems
      sufficient for other browsers. Nobody's following the spec so this is all Calvinball.
      */
      const focused = document.activeElement;
      const preventEvent = event => {
        event.stopPropagation();
      };
      document.addEventListener('focusout', preventEvent, true);
      document.addEventListener('focusin', preventEvent, true);
      dialog.inert = true;
      dialog.show();
      if (focused && document.activeElement !== focused) {
        focused.focus();
      }
      document.removeEventListener('focusout', preventEvent, true);
      document.removeEventListener('focusin', preventEvent, true);
      dialog.inert = false;
    } else {
      dialog.show();
    }
  }
}
function closeDialog(dialog, closeAnimationClassName) {
  if (closeAnimationClassName) {
    dialog.classList.add(closeAnimationClassName);
    const listener = () => {
      dialog.removeEventListener('animationend', listener);
      dialog.close();
      dialog.classList.remove(closeAnimationClassName);
    };
    dialog.addEventListener('animationend', listener);
  } else {
    dialog.close();
  }
}

/**
 * A shared counter of the number of times that the no-scroll class has been added to `html`.
 */
let noscrollCount = 0;

/**
 * Simple wrapper around the `<dialog />` element that utilizes the `show()`/`showModal()` methods
 * per [MDN recommendations](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog).
 */
const Dialog = /*#__PURE__*/forwardRef(function Dialog(_ref, forwardedRef) {
  let {
    children,
    className: callerClassName,
    closeAnimationClassName,
    focusOnOpen = true,
    isOpen,
    modal = false,
    mode = 'default',
    onClose,
    ...props
  } = _ref;
  const ref = useRef(null);
  const mouseDownWithin = useRef(false);
  const className = classNames('dsx-component', callerClassName);
  const modalRef = useRef(false);
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, [setHasMounted]);
  useEffect(() => {
    if (isOpen && ref.current && !ref.current.open) {
      modalRef.current = modal;
      openDialog(ref.current, modal, focusOnOpen);
    }
    if (!isOpen && ref.current && ref.current.open) {
      closeDialog(ref.current, closeAnimationClassName);
    }

    // This effect only needs to run when isOpen has changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEffect(() => {
    if (ref.current && modalRef.current && isOpen) {
      ++noscrollCount;
      const html = document.querySelector('html');
      html.classList.add(styles['no-scroll']);
      return () => {
        --noscrollCount;
        if (noscrollCount === 0) {
          html.classList.remove(styles['no-scroll']);
        }
      };
    }
  }, [isOpen]);
  useEffect(() => {
    var _ref$current;
    if ((_ref$current = ref.current) !== null && _ref$current !== void 0 && _ref$current.open && modal !== modalRef.current) {
      console.warn('Dialog: changing the value of the `modal` prop while a dialog is open is not supported.');
    }
  }, [modal]);
  useEffect(() => {
    return () => {
      if (isOpen && closeAnimationClassName) {
        console.warn('Dialog: unmounting an open Dialog will not perform the closing animation.');
      }
    };
    // This effect should only run on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onKeyDown = event => {
    var _props$onKeyDown;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (mode === 'default') {
        closeDialog(event.currentTarget, closeAnimationClassName);
        onClose === null || onClose === void 0 || onClose(event);
      }
    }
    (_props$onKeyDown = props.onKeyDown) === null || _props$onKeyDown === void 0 || _props$onKeyDown.call(props, event);
  };
  const onMouseDown = event => {
    if (!ref.current) {
      return;
    }
    const dialogRect = ref.current.getBoundingClientRect();
    mouseDownWithin.current = isWithin(dialogRect, event);
  };
  const onMouseUp = event => {
    if (!ref.current) {
      return;
    }

    // when dialogs are nested, the target can be checked to narrow down the
    // specific dialog that should be closed.
    const forThisDialog = event.target === ref.current;

    // the dialog reports mouseup events on the backdrop as well as the dialog itself,
    // the bounding rect is used to see exactly where the click was.
    const dialogRect = ref.current.getBoundingClientRect();
    const mouseUpWithin = isWithin(dialogRect, event);
    const shouldClose = mode !== 'blocking' && forThisDialog && !mouseDownWithin.current && !mouseUpWithin;
    if (shouldClose) {
      closeDialog(event.currentTarget, closeAnimationClassName);
      onClose === null || onClose === void 0 || onClose(event);
    }
  };

  // https://www.matuzo.at/blog/2023/focus-dialog/
  // Browser behavior regarding <dialog> and focus is inconsistent.
  // The spec says: "The tabindex attribute must not be specified on dialog elements."
  // The blog post says: "I won't do what you tell me.  – Rage Against the Machine"
  // Most browsers set a default tabIndex of -1 (if at all).
  // Firefox sets a default tabIndex of 0, which is definitely not what we want.
  const ua = use_UNSAFEUserAgentInfo();
  const tabIndex = ua.browser === 'firefox' ? -1 : undefined;
  return /*#__PURE__*/jsx("dialog", {
    className: className,
    ref: combineRefs(forwardedRef, ref),
    tabIndex: tabIndex,
    onKeyDown: onKeyDown,
    onMouseDown: onMouseDown,
    onMouseUp: onMouseUp,
    ...props,
    children: hasMounted && children
  });
});
function isWithin(rect, event) {
  return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
}

function usePress(args) {
  const {
    onPress
  } = args;
  return {
    pressProps: {
      onClick: e => onPress === null || onPress === void 0 ? void 0 : onPress({
        type: 'click',
        event: e
      }),
      onKeyDown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          onPress === null || onPress === void 0 || onPress({
            type: 'keyDown',
            event: e
          });
        }
      },
      onTouchEnd: e => {
        onPress === null || onPress === void 0 || onPress({
          type: 'touchEnd',
          event: e
        });
      }
    }
  };
}
function useFocus(args) {
  const {
    onFocus,
    onBlur
  } = args;
  return {
    focusProps: {
      onFocus: e => onFocus === null || onFocus === void 0 ? void 0 : onFocus(e),
      onBlur: e => onBlur === null || onBlur === void 0 ? void 0 : onBlur(e)
    }
  };
}

/** Produces a tag to identify key elements of a component without depending on DOM structure. */
function tags(props) {
  for (var _len = arguments.length, segments = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    segments[_key - 1] = arguments[_key];
  }
  const key = ['data-dsx', ...segments].join('--');
  const value = props.tag || '';
  return {
    [key]: value
  };
}

let id = 0;

/**
 * Returns a unique id each time the function is called.
 */
function uniqueId() {
  return `dsx-${id++}`;
}

/**
 * A hook returning a memoized unique id.
 */
function useUniqueId() {
  return useMemo(() => uniqueId(), []);
}

/**
 * Given one or more properly formatted media queries, returns the status of each.
 *
 * @example const [greaterThan640] = useMediaQuery('(min-width: 640px)')
 */
function useMediaQuery() {
  for (var _len = arguments.length, queries = new Array(_len), _key = 0; _key < _len; _key++) {
    queries[_key] = arguments[_key];
  }
  const isSupported = typeof window !== 'undefined' && 'matchMedia' in window;
  const [{
    queryKey,
    mediaQueryLists,
    matches
  }, setState] = useState((() => {
    const queryKey = queries.join('-');
    const mediaQueryLists = queries.map(query => isSupported ? window.matchMedia(query) : null);
    const matches = mediaQueryLists.map(mediaQuerylist => !!(mediaQuerylist !== null && mediaQuerylist !== void 0 && mediaQuerylist.matches));
    return {
      queryKey,
      mediaQueryLists,
      matches
    };
  })() // IIFE because `matches` depends on `mediaQueryLists`
  );

  useLayoutEffect(() => {
    const mediaQueryListToIndex = {};
    mediaQueryLists.forEach((mediaQueryList, index) => {
      if (mediaQueryList !== null) {
        mediaQueryListToIndex[mediaQueryList.media] = index;
      }
    });

    // update return shape if queries changes
    if (queries.join('-') !== queryKey) {
      const newKey = queries.join('-');
      const newMediaQueryLists = queries.map(query => isSupported ? window.matchMedia(query) : null);
      const newMatches = newMediaQueryLists.map(mediaQuerylist => !!(mediaQuerylist !== null && mediaQuerylist !== void 0 && mediaQuerylist.matches));
      setState({
        queryKey: newKey,
        mediaQueryLists: newMediaQueryLists,
        matches: newMatches
      });
    }
    const listener = e => {
      setState(prevState => {
        const nextMatches = prevState.matches.slice();
        nextMatches[mediaQueryListToIndex[e.media]] = e.matches;
        return {
          ...prevState,
          matches: nextMatches
        };
      });
    };
    mediaQueryLists.forEach(mediaQueryList => {
      mediaQueryList === null || mediaQueryList === void 0 || mediaQueryList.addEventListener('change', listener);
    });
    return () => {
      mediaQueryLists.forEach(mediaQueryList => {
        mediaQueryList === null || mediaQueryList === void 0 || mediaQueryList.removeEventListener('change', listener);
      });
    };
  }, [queries, isSupported, queryKey, mediaQueryLists, setState]);
  return matches;
}

/**
 * Given one or more widths in `em` units, returns if the breakpoint applies.
 * @example const [greaterThan24, greaterThan36] = useBreakpointsEm(24, 36)
 */
function useBreakpointsEm() {
  for (var _len = arguments.length, sizesInEms = new Array(_len), _key = 0; _key < _len; _key++) {
    sizesInEms[_key] = arguments[_key];
  }
  const queries = sizesInEms.map(x => `(min-width: ${x}em)`);
  return useMediaQuery(...queries);
}

/**
 * Adds a click event handler to the document which calls onClickOutside() when a click event bubbles
 * to the top level whose target is not within any of the given element(s).
 */
function useClickOutside(refs, onClickOutside) {
  useEffect(() => {
    const validRefs = (Array.isArray(refs) ? refs : [refs]).filter(r => r != null);
    if (!validRefs.length) {
      return;
    }
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
    function handleClick(e) {
      if (!validRefs.some(r => r.contains(e.target))) {
        onClickOutside();
      }
    }
  }, [refs, onClickOutside]);
}

/**
 * Returns a `[value, setValue]` pair as with `useState`, but deferring to a controlled value if present.
 * This can be used to create components that function either in a controlled or uncontrolled mode.
 *
 * In uncontrolled mode (when `controlledValue` is undefined), `value` and `setValue` behave exactly as
 * `setState` given `defaultValue` during initialization.
 * Calling `setValue` will immediately update `value` to match the new value provided, as well as calling
 * the `onUpdate` callback.
 *
 * In controlled mode (when `controlledValue` is defined), `value` is always the same as `controlledValue`.
 * Calling `setValue` will call the `onUpdate` callback with the new value provided.
 */
function useControllableState(controlledValue, defaultValue, onUpdate) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const value = controlledValue ?? uncontrolledValue;
  const callback = value => {
    setUncontrolledValue(value);
    if (onUpdate) {
      onUpdate(value);
    }
  };
  return [value, callback];
}

/**
 * Queries a set of common CSS media features, such as
 * orientation and user display preferences.
 */
function useMediaFeatures() {
  const [orientation, prefersReducedMotion, prefersColorSchemeDark, prefersColorSchemeLight, pointerCoarse, pointerFine] = useMediaQuery('(orientation: landscape)', '(prefers-reduced-motion)', '(prefers-color-scheme: dark)', '(prefers-color-scheme: light)', '(pointer: coarse)', '(pointer: fine)');
  return {
    prefersReducedMotion,
    prefersColorScheme: prefersColorSchemeDark ? 'dark' : prefersColorSchemeLight ? 'light' : 'none',
    orientation: orientation ? 'landscape' : 'portrait',
    pointer: pointerFine ? 'fine' : pointerCoarse ? 'coarse' : 'none'
  };
}

// Blink incorrectly considers leading spaces in CSS property value declarations to be significant:
//   --foo: bar;
//   Firefox, Safari: window.getComputedStyle(el).getPropertyValue('--foo')  => 'bar'
//   Chrome, Edge:    window.getComputedStyle(el).getPropertyValue('--foo')  => ' bar'
// Thus we need to trim values so that different browsers will get the same results.
// Relevant CSS spec:
// https://www.w3.org/TR/css-variables-1/#serializing-custom-props
//   "Note that the leading whitespace on the value is automatically trimmed by the CSS parser; it’s not preserved here."
// https://www.w3.org/TR/CSS21/syndata.html#tokenization
//   declaration : property S* ':' S* value;
function trimValue(value) {
  const maybeTrimmed = typeof value === 'string' ? value.trimStart() : value;
  return maybeTrimmed;
}

/**
 * Retrieves the value of a theme CSS variable.
 */

function useThemeVariable(nameOrNames, ref) {
  const [values, setValues] = useState([]);
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || ref.current == null) {
      return;
    }
    const computedStyle = window.getComputedStyle(ref.current);
    let cssVariableValues;
    if (typeof nameOrNames === 'string') {
      const variableValue = computedStyle.getPropertyValue(`--${nameOrNames}`);
      cssVariableValues = [[nameOrNames, trimValue(variableValue)]];
    } else {
      cssVariableValues = nameOrNames.map(variableName => {
        const variableValue = computedStyle.getPropertyValue(`--${variableName}`);
        return [variableName, trimValue(variableValue)];
      });
    }
    setValues(cssVariableValues);
  }, [nameOrNames, ref]);
  return useMemo(() => {
    var _values$;
    return typeof nameOrNames === 'string' ? (_values$ = values[0]) === null || _values$ === void 0 ? void 0 : _values$[1] : values.reduce((variableRecord, _ref) => {
      let [variableName, variableValue] = _ref;
      variableRecord[variableName] = variableValue;
      return variableRecord;
    }, {});
  }, [nameOrNames, values]);
}

export { Dialog, combineRefs, tags, uniqueId, useBreakpointsEm, useClickOutside, useControllableState, useFocus, useMediaFeatures, useMediaQuery, usePress, useThemeVariable, useUniqueId, use_UNSAFEUserAgentInfo };
